import React, { FC, lazy, Suspense } from 'react';
import * as Sentry from '@sentry/react';

import { ErrorPage, Loaders } from 'components';

const ErrorBoundary: FC = ({ children }) => (
  <Sentry.ErrorBoundary fallback={() => <ErrorPage type="general" />} showDialog>
    {children}
  </Sentry.ErrorBoundary>
);

const accountImport = import(/* webpackPrefetch: true */ 'features/account/containers/Edit');
const activityImport = import(
  /* webpackPrefetch: true */ 'features/activity/containers/ActivityIndex'
);
const adminImport = import(/* webpackPrefetch: true */ 'features/admin/Controller');
const allPostsImport = import(
  /* webpackPrefetch: true */ 'features/timeline/containers/AllPostsIndex'
);
const connectionsSuccess = import(/* webpackPrefetch: true */ 'features/connections/Success');
const connectionsFailure = import(/* webpackPrefetch: true */ 'features/connections/Failure');
const contentPermalinkImport = import(
  /* webpackPrefetch: true */ 'features/content/containers/Permalink'
);
const contentShareImport = import(
  /* webpackPrefetch: true */ 'features/sharing/containers/ContentShare'
);
const editGroupImport = import(/* webpackPrefetch: true */ 'features/groups/containers/Edit');
const groupsIndexImport = import(
  /* webpackPrefetch: true */ 'features/groups/containers/GroupsIndex'
);
const groupImport = import(/* webpackPrefetch: true */ 'features/groups/containers/Group');
const leaderboardImport = import(
  /* webpackPrefetch: true */ 'features/leaderboard/containers/LeaderboardIndex'
);
const newGroupImport = import(/* webpackPrefetch: true */ 'features/groups/containers/New');
const notificationsImport = import(
  /* webpackPrefetch: true */ 'features/notifications/containers/Inbox'
);
const peopleImport = import(/* webpackPrefetch: true */ 'features/people/containers/PeopleIndex');
const profileImport = import(/* webpackPrefetch: true */ 'features/people/containers/Profile');
const searchImport = import(/* webpackPrefetch: true */ 'features/search/containers/Results');
const timelineImport = import(
  /* webpackPrefetch: true */ 'features/timeline/containers/TimelineIndex'
);
const onboardingFollowsImport = import(
  /* webpackPrefetch: true */ 'features/onboarding/containers/Follows'
);
const onboardingProfileImport = import(
  /* webpackPrefetch: true */ 'features/onboarding/containers/Profile'
);
const postImport = import(/* webpackPrefetch: true */ 'features/posting/Controller');
const externalShareImport = import(
  /* webpackPrefetch: true */ 'features/sharing/containers/ExternalShare'
);

const LazyAccountLayout = lazy(() => accountImport);
const LazyActivityLayout = lazy(() => activityImport);
const LazyAdmin = lazy(() => adminImport);
const LazyAllPostsLayout = lazy(() => allPostsImport);
const LazyConnectionsSuccess = lazy(() => connectionsSuccess);
const LazyConnectionsFailure = lazy(() => connectionsFailure);
const LazyContentPermalinkLayout = lazy(() => contentPermalinkImport);
const LazyContentShareLayout = lazy(() => contentShareImport);
const LazyEditGroupLayout = lazy(() => editGroupImport);
const LazyGroupsIndexLayout = lazy(() => groupsIndexImport);
const LazyGroupLayout = lazy(() => groupImport);
const LazyLeaderboardLayout = lazy(() => leaderboardImport);
const LazyNewGroupLayout = lazy(() => newGroupImport);
const LazyNotificationsLayout = lazy(() => notificationsImport);
const LazyPeopleLayout = lazy(() => peopleImport);
const LazyProfileLayout = lazy(() => profileImport);
const LazySearchLayout = lazy(() => searchImport);
const LazyTimelineLayout = lazy(() => timelineImport);
const LazyOnboardingFollowsLayout = lazy(() => onboardingFollowsImport);
const LazyOnboardingProfileLayout = lazy(() => onboardingProfileImport);
const LazyPostLayout = lazy(() => postImport);
const LazyExternalShareLayout = lazy(() => externalShareImport);

export const AccountLayout = () => (
  <ErrorBoundary>
    <Suspense fallback={<Loaders.Standard />}>
      <LazyAccountLayout />
    </Suspense>
  </ErrorBoundary>
);
export const ActivityLayout = () => (
  <ErrorBoundary>
    <Suspense fallback={<Loaders.Standard />}>
      <LazyActivityLayout />
    </Suspense>
  </ErrorBoundary>
);
export const AdminLayout = () => (
  <ErrorBoundary>
    <Suspense fallback={<Loaders.Standard />}>
      <LazyAdmin />
    </Suspense>
  </ErrorBoundary>
);
export const AllPostsLayout = () => (
  <ErrorBoundary>
    <Suspense fallback={<Loaders.Standard />}>
      <LazyAllPostsLayout />
    </Suspense>
  </ErrorBoundary>
);
export const ConnectionsSuccess = () => (
  <ErrorBoundary>
    <Suspense fallback={<Loaders.Standard />}>
      <LazyConnectionsSuccess />
    </Suspense>
  </ErrorBoundary>
);
export const ConnectionsFailure = () => (
  <ErrorBoundary>
    <Suspense fallback={<Loaders.Standard />}>
      <LazyConnectionsFailure />
    </Suspense>
  </ErrorBoundary>
);
export const ContentPermalinkLayout = () => (
  <ErrorBoundary>
    <Suspense fallback={<Loaders.Standard />}>
      <LazyContentPermalinkLayout />
    </Suspense>
  </ErrorBoundary>
);
export const ContentShareLayout = () => (
  <ErrorBoundary>
    <Suspense fallback={<Loaders.Standard />}>
      <LazyContentShareLayout />
    </Suspense>
  </ErrorBoundary>
);
export const TimelineLayout = () => (
  <ErrorBoundary>
    <Suspense fallback={<Loaders.Standard />}>
      <LazyTimelineLayout />
    </Suspense>
  </ErrorBoundary>
);
export const OnboardingFollowsLayout = () => (
  <ErrorBoundary>
    <Suspense fallback={<Loaders.Standard />}>
      <LazyOnboardingFollowsLayout />
    </Suspense>
  </ErrorBoundary>
);
export const OnboardingProfileLayout = () => (
  <ErrorBoundary>
    <Suspense fallback={<Loaders.Standard />}>
      <LazyOnboardingProfileLayout />
    </Suspense>
  </ErrorBoundary>
);
export const GroupsIndexLayout = () => (
  <ErrorBoundary>
    <Suspense fallback={<Loaders.Standard />}>
      <LazyGroupsIndexLayout />
    </Suspense>
  </ErrorBoundary>
);
export const GroupLayout = () => (
  <ErrorBoundary>
    <Suspense fallback={<Loaders.Standard />}>
      <LazyGroupLayout />
    </Suspense>
  </ErrorBoundary>
);
export const LeaderboardLayout = () => (
  <ErrorBoundary>
    <Suspense fallback={<Loaders.Standard />}>
      <LazyLeaderboardLayout />
    </Suspense>
  </ErrorBoundary>
);
export const NewGroupLayout = () => (
  <ErrorBoundary>
    <Suspense fallback={<Loaders.Standard />}>
      <ErrorBoundary>
        <LazyNewGroupLayout />
      </ErrorBoundary>
    </Suspense>
  </ErrorBoundary>
);
export const EditGroupLayout = () => (
  <ErrorBoundary>
    <Suspense fallback={<Loaders.Standard />}>
      <LazyEditGroupLayout />
    </Suspense>
  </ErrorBoundary>
);
export const NotificationsLayout = () => (
  <ErrorBoundary>
    <Suspense fallback={<Loaders.Standard />}>
      <LazyNotificationsLayout />
    </Suspense>
  </ErrorBoundary>
);
export const PeopleLayout = () => (
  <ErrorBoundary>
    <Suspense fallback={<Loaders.Standard />}>
      <LazyPeopleLayout />
    </Suspense>
  </ErrorBoundary>
);
export const ProfileLayout = () => (
  <ErrorBoundary>
    <Suspense fallback={<Loaders.Standard />}>
      <LazyProfileLayout />
    </Suspense>
  </ErrorBoundary>
);
export const SearchLayout = () => (
  <ErrorBoundary>
    <Suspense fallback={<Loaders.Standard />}>
      <LazySearchLayout />
    </Suspense>
  </ErrorBoundary>
);
export const PostLayout = () => (
  <ErrorBoundary>
    <Suspense fallback={<Loaders.Standard />}>
      <LazyPostLayout />
    </Suspense>
  </ErrorBoundary>
);
export const ExternalShareLayout = () => (
  <ErrorBoundary>
    <Suspense fallback={<Loaders.Standard />}>
      <LazyExternalShareLayout />
    </Suspense>
  </ErrorBoundary>
);
